import type { Line2 } from './line2';
import { PlaneRange } from './plane-range';
import { Vec2Like } from './vec-like';
import { Vec2 } from './vec2';

const tmpV2A=new Vec2();
const tmpV2B=new Vec2();

export class Ray2{
	public constructor(
		public org=new Vec2(),
		public dir=new Vec2(),
	){
	}

	public clone():this{
		return new (<any>this.constructor)(this.org.clone(),this.dir.clone());
	}

	protected minDist(){
		return 0;
	}

	public fromPoints(org:Vec2Like, towards:Vec2Like){
		this.org.copy(org);
		this.dir.copy(towards).sub(org).nrm();
		return this;
	}

	public _nrm(out:Vec2){
		out.x=-this.dir.y;
		out.y=this.dir.x;
		return out;
	}

	public nrm(){
		return new Vec2(-this.dir.y,this.dir.x);
	}

	public distOf(p:Vec2Like){
		return this.dir.dot(p)-this.dir.dot(this.org);
	}

	public _atDist<Out extends Vec2>(dist:number, out:Out){
		return out.copy(this.org).addScaled(this.dir,dist);
	}

	public atDist(dist:number){
		return this.org.clone().addScaled(this.dir,dist);
	}

	public _pointToRelative(p:Vec2Like, out:Vec2){
		const x=this.distOf(p);//done this way so that p and out can be the same object
		const nrm=this._nrm(tmpV2A);
		out.y=nrm.dot(p)-nrm.dot(this.org);
		out.x=x;
		return out;
	}

	public _pointFromRelative(p:Vec2Like, out:Vec2){
		tmpV2A.copy(this.org);
		tmpV2A.addScaled(this.dir,p.x);
		tmpV2A.addScaled(this._nrm(tmpV2B),p.y);
		out.copy(tmpV2A);
		return out;
	}

	public _closestPoint(p:Vec2Like, out:Vec2){
		const dist=Math.max(this.minDist(),this.distOf(p));
		return this._atDist(dist,out);
	}

	public closestPoint(p:Vec2Like){
		const dist=Math.max(this.minDist(),this.distOf(p));
		return this.atDist(dist);
	}

	public distToPointSq(p:Vec2Like){
		const a=this.distOf(p);
		const b=this.org.distToSq(p);
		if(a<this.minDist())
			return b;
		return b-a*a;
	}

	public distToPoint(p:Vec2Like){
		return Math.sqrt(this.distToPointSq(p));
	}

	private internalIntersectRayAtDist(that:Ray2){
		const normal=this._nrm(tmpV2B);
		const cos=this.dir.dot(that.dir);
		const sin=normal.dot(that.dir);
		if(sin===0)
			return undefined;

		const delta=tmpV2A.copy(that.org).sub(this.org);
		const distanceAway=normal.dot(delta);
		const distAlongThis=this.dir.dot(delta)-distanceAway*cos/sin;
		return distAlongThis;
	}

	private internalIntersectRay<Out extends Ray2.Intersection>(
		that:Ray2,
		minDistThis:number,
		maxDistThis:number,
		minDistThat:number,
		maxDistThat:number,
		out:Out,
	){
		const atDistThis=this.internalIntersectRayAtDist(that);
		if(atDistThis===undefined || atDistThis<minDistThis || maxDistThis<atDistThis)
			return null;

		this._atDist(atDistThis,out);
		const atDistThat=that.distOf(out);
		if(atDistThat<minDistThat || maxDistThat<atDistThat)
			return null;

		out.distA=atDistThis;
		out.distB=atDistThat;
		return out;
		// return this._intersection(line,out,maxDistance,false);
	}

	public _intersectRay(that:Ray2, out:Ray2.Intersection){
		return this.internalIntersectRay(that,this.minDist(),Infinity,that.minDist(),Infinity,out);
	}

	public intersectRay(that:Ray2){
		return this._intersectRay(that,new Ray2.Intersection());
	}

	public _intersectLine<Out extends Ray2.Intersection>(that:Line2, out:Out){
		tmpRay.org.copy(that.a);
		that._dir(tmpRay.dir);
		return this.internalIntersectRay(tmpRay,this.minDist(),Infinity,0,tmpRay.distOf(that.b),out);
	}

	public intersectLine(that:Line2){
		return this._intersectLine(that,new Ray2.Intersection());
	}

	// public intersection(that:Ray2|Line2):Ray2.Intersection;
	// public intersection<Out extends Vec2>(that:Ray2|Line2<Vec2>, out:Out):Out;
	public _intersection(that:Ray2|Line2, out:Ray2.Intersection){
		if(that instanceof Ray2)
			return this._intersectRay(that,out);
		return this._intersectLine(that,out);
	}

	public intersection(that:Ray2|Line2){
		return this._intersection(that,new Ray2.Intersection());
	}

	public intersects(that:Line2){
		tmpRay.org.copy(that.a);
		that._dir(tmpRay.dir);

		const atDistThis=this.internalIntersectRayAtDist(tmpRay);
		if(atDistThis===undefined || atDistThis<this.minDist())
			return false;

		const _out=this._atDist(atDistThis,tmpV2A);
		const atDistThat=that.distOf(_out);
		const minDistThat=0;
		const maxDistThat=tmpRay.distOf(that.b);
		if(atDistThat<minDistThat || maxDistThat<atDistThat)
			return false;
		return true;
	}

	public intersectionDistsCircle(
		circle:{center:Vec2;radius:number},
		inclusiveCircle:boolean,
	){
		const {center}=circle;
		const rSq=circle.radius**2;
		const t=this.distOf(center);
		const dcSq=this.org.distToSq(center);
		const ecSq=Math.max(0,dcSq-t**2); //due to floating point bullshit if the circle center is on the line you may get an ec value this is very very slightly negative
		if(ecSq<rSq){
			const dt=Math.sqrt(rSq-ecSq);
			return [t-dt,t+dt];
		}else if(ecSq===circle.radius && inclusiveCircle){
			return [t];
		}
		return [];
	}

	public intersectionsCircle(
		circle:{center:Vec2;radius:number},
		inclusiveCircle:boolean,
	){
		const minDist=this.minDist();
		this.intersectionDistsCircle(circle,inclusiveCircle)
			.filter(d=>d>=minDist)
			.map(d=>{
				const p=this._atDist(d,new Ray2.Intersection());
				p.distA=d;
			});
	}

	public planeRange(){
		const nrm=this.dir
		return new PlaneRange(nrm,nrm.dot(this.org),Infinity);
	}
}

function _bestFit<T>(points:Vec2Like[], createRay:(org:Vec2, dir:Vec2)=>T){
	if(points.length<2)
		return undefined;

	const dir=new Vec2(0,0);
	const tmp=new Vec2();
	for(let i=0;i<points.length;++i){
		for(let j=i+1;j<points.length;++j){
			let a=points[i];
			let b=points[j];
			tmp.diff(a,b);
			if(dir.dot(tmp)<0)
				tmp.neg();
			dir.add(tmp);
		}
	}
	dir.nrm();
	const org=new Vec2(0,0);
	for(const p of points)
		org.add(p);
	org.invScale(points.length);
	return createRay(org,dir);
}

export namespace Ray2{
	export function fromPoints(org:Vec2Like, towards:Vec2Like){
		return new Ray2().fromPoints(org,towards);
	}

	export class Intersection extends Vec2{
		distA:number;
		distB:number;
	}

	export function bestFit(points:Vec2Like[]){
		return _bestFit(points,(org,dir)=>new Ray2(org,dir));
	}
}

const tmpRay=new Ray2();

export class TwoSidedRay2 extends Ray2{
	public constructor(
		org?:Vec2,
		dir?:Vec2,
	){
		super(org,dir);
	}

	protected override minDist(){
		return -Infinity;
	}

	public override distToPoint(p:Vec2Like){
		const a=this.distOf(p);
		const b=this.org.distTo(p);
		return Math.sqrt(Math.max(0,b*b-a*a));//can sometimes be negative if the point is on the ray and floating point numbers decide to be stupid --John Lockwood
	}
}

export namespace TwoSidedRay2{
	export function fromPoints(org:Vec2Like, towards:Vec2Like){
		return new TwoSidedRay2().fromPoints(org,towards);
	}

	export function bestFit(points:Vec2Like[]){
		return _bestFit(points,(org,dir)=>new TwoSidedRay2(org,dir));
	}
}
